import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, GripVertical, Upload, Download, ChevronUp, ChevronDown } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api, { money, apiError } from "@/lib/api";
import ImageCropModal from "@/components/admin/ImageCropModal";
import { toast } from "sonner";

const empty = { name: "", description: "", price: 0, image: "", category_id: "", popular: false, new: false, available: true, is_recommended: false, dietary: [], allergens: [], modifier_groups: [] };

function ModifierEditor({ groups, setGroups }) {
  const addGroup = () => setGroups([...groups, { id: crypto.randomUUID(), name: "New Group", type: "single", required: false, min: 0, max: 1, options: [] }]);
  const upd = (i, patch) => setGroups(groups.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  const addOpt = (i) => upd(i, { options: [...groups[i].options, { id: crypto.randomUUID(), name: "Option", price: 0 }] });
  const updOpt = (i, oi, patch) => upd(i, { options: groups[i].options.map((o, x) => (x === oi ? { ...o, ...patch } : o)) });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between"><h4 className="font-bold text-sm">Modifier Groups</h4><button type="button" onClick={addGroup} className="text-xs font-semibold text-metros-red flex items-center gap-1"><Plus size={13} /> Add group</button></div>
      {groups.map((g, i) => (
        <div key={g.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
          <div className="flex gap-2">
            <input value={g.name} onChange={(e) => upd(i, { name: e.target.value })} className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
            <select value={g.type} onChange={(e) => upd(i, { type: e.target.value })} className="rounded-lg border border-gray-200 px-2 text-sm"><option value="single">Single</option><option value="multi">Multi</option></select>
            <button type="button" onClick={() => setGroups(groups.filter((_, x) => x !== i))} className="text-gray-400 hover:text-metros-red"><Trash2 size={16} /></button>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1"><input type="checkbox" checked={g.required} onChange={(e) => upd(i, { required: e.target.checked })} /> Required</label>
            <label>Max <input type="number" value={g.max} onChange={(e) => upd(i, { max: +e.target.value })} className="w-12 rounded border border-gray-200 px-1" /></label>
          </div>
          {g.options.map((o, oi) => (
            <div key={o.id} className="flex gap-2">
              <input value={o.name} onChange={(e) => updOpt(i, oi, { name: e.target.value })} className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-sm" />
              <input type="number" step="0.5" value={o.price} onChange={(e) => updOpt(i, oi, { price: +e.target.value })} className="w-20 rounded-lg border border-gray-200 px-2 py-1 text-sm" />
              <button type="button" onClick={() => upd(i, { options: g.options.filter((_, x) => x !== oi) })} className="text-gray-400"><X size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={() => addOpt(i)} className="text-xs text-metros-red">+ option</button>
        </div>
      ))}
    </div>
  );
}

export default function AdminMenu() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [newCat, setNewCat] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [visData, setVisData] = useState({ categories: [], visible: [] });
  const [branchList, setBranchList] = useState([]);
  const [srcBranch, setSrcBranch] = useState("");
  const [tgtBranch, setTgtBranch] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const dragIdx = React.useRef(null);

  const reorderList = (list, from, to) => { const a = [...list]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; };
  const handleDrop = async (setList, list, to, endpoint) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from === null || from === to) return;
    const next = reorderList(list, from, to);
    setList(next);
    await api.post(endpoint, { ids: next.map((x) => x.id) });
    toast.success("Order updated");
  };

  const move = async (setList, list, index, dir, endpoint) => {
    const to = index + dir;
    if (to < 0 || to >= list.length) return;
    const next = reorderList(list, index, to);
    setList(next);
    await api.post(endpoint, { ids: next.map((x) => x.id) });
    toast.success("Order updated");
  };

  const load = () => {
    api.get("/admin/products").then(({ data }) => setProducts(data));
    api.get("/admin/categories").then(({ data }) => setCats(data));
    api.get("/admin/branch-categories").then(({ data }) => setVisData(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/admin/menu/branches").then(({ data }) => {
      setBranchList(data || []);
      if (data?.[0]) { setSrcBranch(data[0].id); setTgtBranch((data[1] || data[0]).id); }
    }).catch(() => {});
  }, []);

  const exportMenu = async () => {
    if (!srcBranch) return;
    try {
      const { data } = await api.get(`/admin/menu/export?branch_id=${srcBranch}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `metros-menu-${srcBranch}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${data.products?.length || 0} items`);
    } catch { toast.error("Could not export menu"); }
  };

  const importMenu = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !tgtBranch) return;
    setCopyBusy(true);
    try {
      const data = JSON.parse(await file.text());
      const { data: res } = await api.post("/admin/menu/import", { target_branch_id: tgtBranch, data, mode: "add" });
      toast.success(`Added ${res.added} items${res.categories_added ? ` + ${res.categories_added} categories` : ""} to this branch`);
      load();
    } catch (err) {
      toast.error(err instanceof SyntaxError ? "That file isn't valid JSON" : apiError(err));
    } finally { setCopyBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/menu/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "metros-menu-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch { toast.error("Could not download template"); }
  };

  const uploadBulk = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/admin/menu/bulk-upload", fd);
      toast.success(`Added ${res.added}, updated ${res.updated} item${res.added + res.updated === 1 ? "" : "s"}${res.categories_added ? ` · ${res.categories_added} new categories` : ""}`);
      if (res.errors?.length) toast.warning(`${res.errors.length} row issue${res.errors.length === 1 ? "" : "s"}: ${res.errors[0]}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBulkBusy(false); }
  };

  const openNew = () => { setForm({ ...empty, category_id: cats[0]?.id || "" }); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      // Downscale the source so the cropper stays snappy; final crop is compressed on apply.
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX / width, MAX / height);
          width = Math.round(width * ratio); height = Math.round(height * ratio);
        }
        try {
          const canvas = document.createElement("canvas");
          canvas.width = width; canvas.height = height;
          canvas.getContext("2d").drawImage(img, 0, 0, width, height);
          setCropSrc(canvas.toDataURL("image/jpeg", 0.9));
        } catch {
          setCropSrc(reader.result);
        }
      };
      img.onerror = () => setCropSrc(reader.result);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async () => {
    const payload = { ...form, price: +form.price };
    if (editing === "new") await api.post("/admin/products", payload);
    else await api.put(`/admin/products/${editing}`, payload);
    toast.success("Saved"); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/admin/products/${id}`); load(); toast.success("Deleted"); };
  const toggle = async (p, field) => { await api.put(`/admin/products/${p.id}`, { [field]: !p[field] }); load(); };

  const addCat = async () => { if (!newCat) return; await api.post("/admin/categories", { name: newCat }); setNewCat(""); load(); toast.success("Category added"); };
  const renameCat = async (c, name) => {
    const n = (name || "").trim();
    if (!n || n === c.name) return;
    await api.put(`/admin/categories/${c.id}`, { name: n });
    load(); toast.success("Category renamed");
  };
  const delCat = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };

  const toggleVisible = async (slug) => {
    const cur = visData.visible.includes(slug)
      ? visData.visible.filter((s) => s !== slug)
      : [...visData.visible, slug];
    if (cur.length === 0) { toast.error("Keep at least one category visible"); return; }
    setVisData({ ...visData, visible: cur });
    try { await api.put("/admin/branch-categories", { visible: cur }); toast.success("Menu visibility updated"); }
    catch { toast.error("Could not update visibility"); load(); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading font-extrabold text-3xl">Menu</h1>
        {tab === "products" && <button data-testid="add-product" onClick={openNew} className="bg-metros-red text-white rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-1"><Plus size={16} /> Add Product</button>}
      </div>
      <div className="flex gap-2 mb-5">
        {["products", "categories", "visibility", "copy", "bulk"].map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`menu-tab-${t}`} className={`px-4 py-2 rounded-full text-sm font-semibold capitalize ${tab === t ? "bg-metros-black text-white" : "bg-white border border-gray-200"}`}>{t === "copy" ? "Copy menu" : t === "bulk" ? "Bulk upload" : t}</button>)}
      </div>

      {tab === "products" ? (
        <div className="grid gap-3">
          <p className="text-xs text-muted-foreground">Use the <ChevronUp size={12} className="inline" /><ChevronDown size={12} className="inline" /> arrows or drag <GripVertical size={12} className="inline" /> to reorder how products appear on the menu.</p>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-muted-foreground">Filter by category</label>
            <select data-testid="product-filter-cat" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="rounded-full border border-gray-200 px-3 py-1.5 text-sm">
              <option value="all">All categories</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            {filterCat !== "all" && <span className="text-xs text-muted-foreground">Reordering is disabled while filtered.</span>}
          </div>
          {products.map((p, i) => {
            if (filterCat !== "all" && p.category_name !== filterCat) return null;
            return (
            <div key={p.id} data-testid={`admin-product-${p.id}`}
              draggable
              onDragStart={() => { dragIdx.current = i; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(setProducts, products, i, "/admin/products/reorder")}
              className="bg-white border border-gray-100 rounded-2xl p-3 flex items-center gap-3">
              <button data-testid={`drag-product-${p.id}`} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none"><GripVertical size={18} /></button>
              <div className="flex flex-col -my-1">
                <button data-testid={`move-up-product-${p.id}`} onClick={() => move(setProducts, products, i, -1, "/admin/products/reorder")} disabled={i === 0} className="text-gray-400 hover:text-metros-black disabled:opacity-30 disabled:cursor-not-allowed" title="Move up"><ChevronUp size={16} /></button>
                <button data-testid={`move-down-product-${p.id}`} onClick={() => move(setProducts, products, i, 1, "/admin/products/reorder")} disabled={i === products.length - 1} className="text-gray-400 hover:text-metros-black disabled:opacity-30 disabled:cursor-not-allowed" title="Move down"><ChevronDown size={16} /></button>
              </div>
              <img src={p.image} alt={p.name} className="h-14 w-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.category_name} · {money(p.price)}</p>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex flex-col items-center gap-1 text-[10px] font-semibold text-muted-foreground">Popular<Switch checked={p.popular} onCheckedChange={() => toggle(p, "popular")} /></label>
                <label className="flex flex-col items-center gap-1 text-[10px] font-semibold text-muted-foreground">Recommend<Switch data-testid={`rec-${p.id}`} checked={p.is_recommended} onCheckedChange={() => toggle(p, "is_recommended")} /></label>
                <label className="flex flex-col items-center gap-1 text-[10px] font-semibold text-muted-foreground">Available<Switch data-testid={`avail-${p.id}`} checked={p.available} onCheckedChange={() => toggle(p, "available")} /></label>
                <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 rounded-full hover:bg-gray-100"><Pencil size={16} /></button>
                <button onClick={() => del(p.id)} className="p-2 rounded-full hover:bg-gray-100 text-metros-red"><Trash2 size={16} /></button>
              </div>
            </div>
            );
          })}
        </div>
      ) : tab === "categories" ? (
        <div className="max-w-lg">
          <div className="flex gap-2 mb-4">
            <input data-testid="new-cat" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm" />
            <button data-testid="add-cat" onClick={addCat} className="bg-metros-red text-white rounded-full px-5 text-sm font-bold">Add</button>
          </div>
          <div className="space-y-2">
            {cats.map((c, i) => (
              <div key={c.id} data-testid={`admin-cat-${c.id}`}
                draggable
                onDragStart={() => { dragIdx.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(setCats, cats, i, "/admin/categories/reorder")}
                className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-2">
                <button data-testid={`drag-cat-${c.id}`} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none"><GripVertical size={16} /></button>
                <div className="flex flex-col -my-1">
                  <button data-testid={`move-up-cat-${c.id}`} onClick={() => move(setCats, cats, i, -1, "/admin/categories/reorder")} disabled={i === 0} className="text-gray-400 hover:text-metros-black disabled:opacity-30 disabled:cursor-not-allowed" title="Move up"><ChevronUp size={14} /></button>
                  <button data-testid={`move-down-cat-${c.id}`} onClick={() => move(setCats, cats, i, 1, "/admin/categories/reorder")} disabled={i === cats.length - 1} className="text-gray-400 hover:text-metros-black disabled:opacity-30 disabled:cursor-not-allowed" title="Move down"><ChevronDown size={14} /></button>
                </div>
                <input data-testid={`cat-name-${c.id}`} defaultValue={c.name} onBlur={(e) => renameCat(c, e.target.value)} className="flex-1 min-w-0 font-semibold text-sm rounded-lg border border-transparent hover:border-gray-200 focus:border-metros-red px-2 py-1 focus:outline-none" title="Click to rename" />
                <button onClick={() => delCat(c.id)} className="text-gray-400 hover:text-metros-red"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      ) : tab === "visibility" ? (
        <div className="max-w-lg" data-testid="visibility-panel">
          <p className="text-sm text-muted-foreground mb-4">Choose which categories show on <b>your branch's</b> menu. Hidden categories (and their products) won't appear to customers ordering from this branch.</p>
          <div className="space-y-2">
            {visData.categories.map((c) => (
              <div key={c.slug} data-testid={`vis-row-${c.slug}`} className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-2">
                <span className="flex-1 font-semibold text-sm">{c.name}</span>
                <Switch data-testid={`vis-toggle-${c.slug}`} checked={visData.visible.includes(c.slug)} onCheckedChange={() => toggleVisible(c.slug)} />
              </div>
            ))}
            {visData.categories.length === 0 && <p className="text-sm text-muted-foreground">No categories yet.</p>}
          </div>
        </div>
      ) : null}

      {tab === "copy" && (
        <div className="max-w-lg space-y-6" data-testid="copy-panel">
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><Download size={16} className="text-metros-red" /> Download a branch menu</h3>
            <p className="text-sm text-muted-foreground mb-3">Saves the whole menu — items, prices, options and pictures — as an editable <b>.json</b> file you can open in any text editor.</p>
            <div className="flex items-center gap-2">
              <select data-testid="copy-src-branch" value={srcBranch} onChange={(e) => setSrcBranch(e.target.value)} className="flex-1 rounded-full border border-gray-200 px-3 py-2 text-sm">
                {branchList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <button data-testid="copy-download" onClick={exportMenu} className="bg-metros-black text-white rounded-full px-5 py-2 text-sm font-bold flex items-center gap-1 whitespace-nowrap"><Download size={15} /> Download</button>
            </div>
          </div>
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><Upload size={16} className="text-metros-red" /> Upload into a branch</h3>
            <p className="text-sm text-muted-foreground mb-3">Adds the file's items to the selected branch (existing items are kept). Pictures are included. Edit the file first if you want to tweak anything.</p>
            <div className="flex items-center gap-2">
              <select data-testid="copy-tgt-branch" value={tgtBranch} onChange={(e) => setTgtBranch(e.target.value)} className="flex-1 rounded-full border border-gray-200 px-3 py-2 text-sm">
                {branchList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <label data-testid="copy-upload-label" className={`cursor-pointer rounded-full px-5 py-2 text-sm font-bold flex items-center gap-1 whitespace-nowrap ${copyBusy ? "bg-gray-300 text-white cursor-wait" : "bg-metros-red text-white"}`}>
                <Upload size={15} /> {copyBusy ? "Uploading…" : "Upload file"}
                <input data-testid="copy-upload" type="file" accept="application/json,.json" className="hidden" disabled={copyBusy} onChange={importMenu} />
              </label>
            </div>
          </div>
        </div>
      )}

      {tab === "bulk" && (
        <div className="max-w-lg space-y-6" data-testid="bulk-panel">
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><Download size={16} className="text-metros-red" /> 1. Download the Excel template</h3>
            <p className="text-sm text-muted-foreground mb-3">Comes <b>pre-filled with your branch's current menu</b> — edit the rows in place instead of retyping. Columns cover item name, description, price, category and more. One item per row.</p>
            <button data-testid="bulk-download-template" onClick={downloadTemplate} className="bg-metros-black text-white rounded-full px-5 py-2 text-sm font-bold flex items-center gap-1 whitespace-nowrap"><Download size={15} /> Download template</button>
          </div>
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><Upload size={16} className="text-metros-red" /> 2. Upload your completed file</h3>
            <p className="text-sm text-muted-foreground mb-3">Adds new items and updates existing ones for <b>your branch</b> (matched by item name). New categories are created automatically.</p>
            <label data-testid="bulk-upload-label" className={`cursor-pointer rounded-full px-5 py-2 text-sm font-bold inline-flex items-center gap-1 whitespace-nowrap ${bulkBusy ? "bg-gray-300 text-white cursor-wait" : "bg-metros-red text-white"}`}>
              <Upload size={15} /> {bulkBusy ? "Uploading…" : "Upload Excel file"}
              <input data-testid="bulk-upload" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" disabled={bulkBusy} onChange={uploadBulk} />
            </label>
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto bg-white rounded-3xl" data-testid="product-editor">
          <h2 className="font-heading font-extrabold text-xl">{editing === "new" ? "Add Product" : "Edit Product"}</h2>
          <div className="space-y-3 mt-2">
            <input data-testid="pf-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm h-16 resize-none" />
            <div className="flex gap-2">
              <input data-testid="pf-price" type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-28 rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="flex-1 rounded-xl border border-gray-200 px-3 text-sm">
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <input data-testid="pf-image" placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <div className="flex items-center gap-3 flex-wrap">
              <label data-testid="pf-image-upload-label" className="cursor-pointer inline-flex items-center gap-2 border border-gray-200 rounded-full px-4 py-2 text-sm font-semibold hover:border-metros-red transition-colors">
                <Upload size={15} /> Upload image
                <input data-testid="pf-image-upload" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
              {form.image && (
                <button type="button" data-testid="pf-image-edit-crop" onClick={() => setCropSrc(form.image)}
                  className="inline-flex items-center gap-2 border border-gray-200 rounded-full px-4 py-2 text-sm font-semibold hover:border-metros-red transition-colors">
                  <Pencil size={14} /> Edit crop
                </button>
              )}
            </div>
            {form.image && (
              <div data-testid="pf-image-preview" className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground">How it appears on the menu</p>
                <div className="w-full max-w-[320px] h-44 rounded-2xl overflow-hidden border border-gray-200 bg-gray-100">
                  <img src={form.image} alt="menu preview" className="h-full w-full object-cover" />
                </div>
              </div>
            )}
            <input placeholder="Dietary (comma separated)" value={form.dietary.join(", ")} onChange={(e) => setForm({ ...form, dietary: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <input placeholder="Allergens (comma separated)" value={form.allergens.join(", ")} onChange={(e) => setForm({ ...form, allergens: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <div className="flex gap-4 text-sm flex-wrap">
              <label className="flex items-center gap-2">Popular <Switch checked={form.popular} onCheckedChange={(v) => setForm({ ...form, popular: v })} /></label>
              <label className="flex items-center gap-2">New <Switch checked={form.new} onCheckedChange={(v) => setForm({ ...form, new: v })} /></label>
              <label className="flex items-center gap-2">Recommended <Switch data-testid="pf-recommended" checked={form.is_recommended} onCheckedChange={(v) => setForm({ ...form, is_recommended: v })} /></label>
              <label className="flex items-center gap-2">Available <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} /></label>
            </div>
            <ModifierEditor groups={form.modifier_groups} setGroups={(g) => setForm({ ...form, modifier_groups: g })} />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="flex-1 border border-gray-200 rounded-full py-2.5 font-semibold text-sm">Cancel</button>
            <button data-testid="save-product" onClick={save} className="flex-1 bg-metros-red text-white rounded-full py-2.5 font-bold text-sm">Save</button>
          </div>
        </DialogContent>
      </Dialog>

      <ImageCropModal
        open={!!cropSrc}
        src={cropSrc}
        onCancel={() => setCropSrc(null)}
        onCropped={(dataUrl) => { setForm((f) => ({ ...f, image: dataUrl })); setCropSrc(null); toast.success("Image cropped"); }}
      />
    </div>
  );
}
